import type { ComponentProps } from 'react';

import { cn } from '../cn';
import { General, Shapes } from '../StrokeIcons';
import type { Size } from './types';

export type SingleCheckboxProps = Omit<ComponentProps<'button'>, 'size' | 'onChange'> & {
  size?: Size;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
};

// Checkbox size (slightly smaller than icon class for better proportions)
function checkboxSize(size: Size = 'md') {
  return {
    '3xs': 'size-2.5',
    '2xs': 'size-3',
    xs: 'size-3.5',
    sm: 'size-4',
    md: 'size-5',
    lg: 'size-6',
    xl: 'size-7',
    '2xl': 'size-8',
    '3xl': 'size-9',
    '4xl': 'size-10',
  }[size];
}

export default function SingleCheckbox({
  size = 'md',
  checked = false,
  indeterminate = false,
  disabled,
  onChange,
  className,
  ...props
}: SingleCheckboxProps) {
  const isActive = checked || indeterminate;

  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={indeterminate ? 'mixed' : checked}
      disabled={disabled}
      onClick={() => onChange?.(!checked)}
      className={cn(
        'inline-flex shrink-0 items-center justify-center transition-colors',
        checkboxSize(size),
        '[&>svg]:size-full',
        isActive && 'text-astronaut-500 dark:text-astronaut-400',
        !isActive && 'text-slate-400 dark:text-slate-500',
        disabled && 'opacity-50 cursor-not-allowed',
        !disabled && 'cursor-pointer',
        !disabled && !isActive && 'hover:text-slate-500 dark:hover:text-slate-400',
        !disabled && isActive && 'hover:text-astronaut-600 dark:hover:text-astronaut-300',
        className,
      )}
      {...props}>
      {indeterminate && <General.MinusSquare />}
      {!indeterminate && checked && <General.CheckSquare />}
      {!indeterminate && !checked && <Shapes.Square />}
    </button>
  );
}
